import { Injectable, inject, signal } from '@angular/core';
import { OfflineOrderService } from './offline-order.service';

/**
 * Estado de conectividad y de la cola de sincronización (F2, Inc.4). Alimenta el
 * banner online/offline del POS. `online` sale de navigator.onLine + eventos;
 * `pending` = órdenes en el outbox aún sin sincronizar. Ver docs/140.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private offlineOrders = inject(OfflineOrderService);
  readonly online = signal<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
  );
  /** Cantidad de órdenes pendientes de sincronizar (PENDING) en el outbox. */
  readonly pending = signal<number>(0);
  /** Cantidad de órdenes RECHAZADAS por el servidor (FAILED) — requieren atención. */
  readonly failed = signal<number>(0);
  /**
   * F2.2 — Ventas de OTRO negocio que esperan en este navegador. No se envían
   * con la sesión abierta; solo salen cuando alguien entra con su cuenta.
   */
  readonly deOtrosNegocios = signal<number>(0);
  /** F4.9b — de las rechazadas, cuántas no se arreglan reintentando (cliente en insolvencia…). */
  readonly rechazadasDefinitivas = signal<number>(0);
  /** F2.3 — con sesión de vendedor: ventas de otro vendedor que esperan en este equipo. */
  readonly deOtrosVendedores = signal<number>(0);
  /** M34 A7 — ventas con factura electrónica que llevan más de 24 h sin subirse. */
  readonly facturasAtrasadas = signal<number>(0);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.online.set(true));
      window.addEventListener('offline', () => this.online.set(false));
    }
  }

  /** Relee los contadores del outbox. Lo llama el SyncScheduler tras cada pasada. */
  async refresh(): Promise<void> {
    try {
      // PENDING + IN_PROGRESS: un evento reclamado por un pase de sync sigue
      // siendo, para el cajero, una orden pendiente. Contar solo PENDING hacía
      // que el número bajara o se quedara corto mientras había envíos en curso.
      // F2.2: solo las del negocio de la sesión abierta; las de otros, aparte.
      const { pendientes, rechazadas, deOtrosNegocios, rechazadasDefinitivas, deOtrosVendedores, facturasAtrasadas } = await this.offlineOrders.contadores();
      this.facturasAtrasadas.set(facturasAtrasadas ?? 0);
      this.deOtrosVendedores.set(deOtrosVendedores);
      this.rechazadasDefinitivas.set(rechazadasDefinitivas);
      this.pending.set(pendientes);
      this.failed.set(rechazadas);
      this.deOtrosNegocios.set(deOtrosNegocios);
    } catch {
      /* ignore */
    }
  }
}
